// CollaboratorDetailModal — Popup detail view for directory entities
// Used from both DirectoryBrowser and EcosystemMarketplace
import { useState, useEffect } from 'react'
import {
  X, FileText, Quote, Hash, ExternalLink, MapPin, Globe, Loader2,
  Link2, GraduationCap, Building2, Banknote, Users, ChevronDown,
} from 'lucide-react'
import type { DirectoryEntity, DirectoryPublication, DirectoryCoAuthor } from '../../types'
import { getDirectoryEntity, getDirectoryPublications, getDirectoryCoAuthors } from '../../services/api'
import { openAlexUrl } from '../../utils/externalLinks'
import ProjectSelectorModal from './ProjectSelectorModal'

interface CollaboratorDetailModalProps {
  isOpen: boolean
  onClose: () => void
  /** Pass a full entity or just an entity_id to fetch on open */
  entity?: DirectoryEntity | null
  entityId?: string
}

function EntityTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'researcher': return <GraduationCap className="w-5 h-5 text-iflow-500" />
    case 'institution': return <Building2 className="w-5 h-5 text-purple-500" />
    case 'corporate': return <Building2 className="w-5 h-5 text-orange-500" />
    case 'funding_body': return <Banknote className="w-5 h-5 text-green-500" />
    default: return <Users className="w-5 h-5 text-gray-500" />
  }
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  researcher: 'Researcher',
  institution: 'Institution',
  corporate: 'Corporate',
  funding_body: 'Funding Body',
}

export default function CollaboratorDetailModal({
  isOpen, onClose, entity: passedEntity, entityId,
}: CollaboratorDetailModalProps) {
  const [entity, setEntity] = useState<DirectoryEntity | null>(passedEntity || null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [publications, setPublications] = useState<DirectoryPublication[]>([])
  const [pubTotal, setPubTotal] = useState(0)
  const [pubPage, setPubPage] = useState(1)
  const [pubLoading, setPubLoading] = useState(false)
  const [showPubs, setShowPubs] = useState(false)
  // BUG-154: track fetch failures so a network/API error is shown as a distinct,
  // retryable state instead of masquerading as an authoritative "no publications".
  const [pubError, setPubError] = useState(false)

  const [coAuthors, setCoAuthors] = useState<DirectoryCoAuthor[]>([])
  const [coAuthorsLoading, setCoAuthorsLoading] = useState(false)
  const [showCoAuthors, setShowCoAuthors] = useState(false)
  const [coAuthorsError, setCoAuthorsError] = useState(false)


  const [linkModalOpen, setLinkModalOpen] = useState(false)

  // Load entity if only ID provided
  useEffect(() => {
    if (!isOpen) return
    if (passedEntity) {
      setEntity(passedEntity)
      setError(null)
      return
    }
    if (entityId) {
      setLoading(true)
      setError(null)
      getDirectoryEntity(entityId)
        .then(setEntity)
        .catch(() => setError('Failed to load entity details.'))
        .finally(() => setLoading(false))
    }
  }, [isOpen, passedEntity, entityId])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPublications([])
      setPubTotal(0)
      setPubPage(1)
      setShowPubs(false)
      setCoAuthors([])
      setShowCoAuthors(false)
      setLinkModalOpen(false)
      setPubError(false)
      setCoAuthorsError(false)
    }
  }, [isOpen])

  // Load publications on demand
  const loadPublications = () => {
    if (!entity) return
    setShowPubs(true)
    if (publications.length > 0) return
    setPubLoading(true)
    setPubError(false)
    getDirectoryPublications(entity.entity_id, 1, 5)
      .then((r) => { setPublications(r.publications || []); setPubTotal(r.total || 0) })
      .catch(() => setPubError(true))
      .finally(() => setPubLoading(false))
  }

  const loadMorePubs = () => {
    if (!entity) return
    const nextPage = pubPage + 1
    setPubLoading(true)
    setPubError(false)
    getDirectoryPublications(entity.entity_id, nextPage, 5)
      .then((r) => {
        setPublications((prev) => [...prev, ...(r.publications || [])])
        setPubTotal(r.total || 0)
        setPubPage(nextPage)
      })
      .catch(() => setPubError(true))
      .finally(() => setPubLoading(false))
  }

  // Load co-authors on demand
  const loadCoAuthors = () => {
    if (!entity) return
    setShowCoAuthors(true)
    if (coAuthors.length > 0) return
    setCoAuthorsLoading(true)
    setCoAuthorsError(false)
    getDirectoryCoAuthors(entity.entity_id)
      .then((r) => setCoAuthors(r.co_authors || []))
      .catch(() => setCoAuthorsError(true))
      .finally(() => setCoAuthorsLoading(false))
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[5vh] overflow-y-auto">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 mb-8 flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
            <h2 className="text-base font-bold text-gray-900">Collaborator Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content — scrollable */}
          <div className="overflow-y-auto flex-1 p-5 space-y-5">
            {loading && (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-iflow-500 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Loading details...</p>
              </div>
            )}

            {error && (
              <div className="py-12 text-center">
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}

            {entity && !loading && (
              <>
                {/* Identity section */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-iflow-50 flex items-center justify-center shrink-0">
                    <EntityTypeIcon type={entity.entity_type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900">{entity.canonical_name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-iflow-50 text-iflow-700">
                        {ENTITY_TYPE_LABELS[entity.entity_type] || entity.entity_type}
                      </span>
                    </div>
                    {entity.affiliation_name && (
                      <p className="text-sm text-gray-500 mt-1">{entity.affiliation_name}</p>
                    )}
                  </div>
                </div>

                {/* Location */}
                {(entity.city || entity.region || entity.country_code) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-2.5">
                    <MapPin className="w-4 h-4 text-gray-500 shrink-0" />
                    <span>{[entity.city, entity.region, entity.country_code].filter(Boolean).join(', ')}</span>
                  </div>
                )}

                {/* Metrics */}
                {(entity.paper_count || entity.citation_count || entity.h_index) && (
                  <div className="grid grid-cols-3 gap-3">
                    {entity.paper_count != null && (
                      <div className="bg-iflow-50/50 rounded-lg p-3 text-center">
                        <FileText className="w-4 h-4 text-iflow-500 mx-auto mb-1" />
                        <div className="text-lg font-bold text-gray-900">{entity.paper_count.toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider">Papers</div>
                      </div>
                    )}
                    {entity.citation_count != null && (
                      <div className="bg-iflow-50/50 rounded-lg p-3 text-center">
                        <Quote className="w-4 h-4 text-iflow-500 mx-auto mb-1" />
                        <div className="text-lg font-bold text-gray-900">{entity.citation_count.toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider">Citations</div>
                      </div>
                    )}
                    {entity.h_index != null && (
                      <div className="bg-iflow-50/50 rounded-lg p-3 text-center">
                        <Hash className="w-4 h-4 text-iflow-500 mx-auto mb-1" />
                        <div className="text-lg font-bold text-gray-900">{entity.h_index}</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider">h-index</div>
                      </div>
                    )}
                  </div>
                )}

                {/* External IDs & Links */}
                {(entity.orcid || entity.ror_id || entity.openalex_id) && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">External Profiles</h4>
                    <div className="flex flex-wrap gap-2">
                      {entity.orcid && (
                        <a href={`https://orcid.org/${entity.orcid}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-iflow-600 bg-iflow-50 rounded-lg hover:bg-iflow-100 transition-colors">
                          <ExternalLink className="w-3 h-3" /> ORCID
                        </a>
                      )}
                      {entity.ror_id && (
                        <a href={`https://ror.org/${entity.ror_id}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-iflow-600 bg-iflow-50 rounded-lg hover:bg-iflow-100 transition-colors">
                          <Globe className="w-3 h-3" /> ROR
                        </a>
                      )}
                      {entity.openalex_id && (
                        <a href={openAlexUrl(entity.openalex_id)} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-iflow-600 bg-iflow-50 rounded-lg hover:bg-iflow-100 transition-colors">
                          <ExternalLink className="w-3 h-3" /> OpenAlex
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Topics */}
                {entity.topics && entity.topics.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Research Topics</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {entity.topics.map((topic) => (
                        <span key={topic} className="text-[11px] px-2.5 py-1 bg-sky-500/10 text-gray-700 rounded-full">
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Publications — expandable */}
                <div className="border border-gray-100 rounded-lg">
                  <button
                    onClick={loadPublications}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors rounded-lg"
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      Publications {pubTotal > 0 && <span className="text-gray-500 font-normal">({pubTotal})</span>}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showPubs ? 'rotate-180' : ''}`} />
                  </button>
                  {showPubs && (
                    <div className="px-4 pb-3 border-t border-gray-50">
                      {pubLoading && publications.length === 0 ? (
                        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading publications...
                        </div>
                      ) : pubError && publications.length === 0 ? (
                        <div className="py-3 text-sm" data-testid="publications-error">
                          <p className="text-red-500">Couldn't load publications.</p>
                          <button
                            onClick={loadPublications}
                            className="mt-1 text-xs font-medium text-iflow-600 hover:text-iflow-700"
                          >
                            Retry
                          </button>
                        </div>
                      ) : publications.length === 0 ? (
                        <p className="text-sm text-gray-500 italic py-3">No publications found.</p>
                      ) : (
                        <div className="space-y-2 pt-2">
                          {publications.map((pub, i) => (
                            <div key={`${pub.title}-${i}`} className="flex items-start gap-2 py-2 border-b border-gray-50 last:border-0">
                              <FileText className="w-3.5 h-3.5 text-gray-300 mt-0.5 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-gray-800 line-clamp-2">
                                  {pub.doi ? (
                                    <a href={`https://doi.org/${pub.doi}`} target="_blank" rel="noopener noreferrer" className="hover:text-iflow-600">
                                      {pub.title}
                                    </a>
                                  ) : pub.title}
                                </p>
                                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500">
                                  {pub.year && <span>{pub.year}</span>}
                                  {pub.journal && <span>{pub.journal}</span>}
                                  {pub.citation_count != null && <span>{pub.citation_count} cit.</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                          {pubError && (
                            <p className="text-xs text-red-500 text-center py-1" data-testid="publications-loadmore-error">
                              Couldn't load more — tap to retry.
                            </p>
                          )}
                          {publications.length < pubTotal && (
                            <button
                              onClick={loadMorePubs}
                              disabled={pubLoading}
                              className="w-full py-2 text-xs font-medium text-iflow-600 hover:bg-iflow-50 rounded-lg transition-colors disabled:opacity-50"
                            >
                              {pubLoading ? 'Loading...' : `Show more (${pubTotal - publications.length} remaining)`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Co-Authors — expandable */}
                <div className="border border-gray-100 rounded-lg">
                  <button
                    onClick={loadCoAuthors}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors rounded-lg"
                  >
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      Co-Authors
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showCoAuthors ? 'rotate-180' : ''}`} />
                  </button>
                  {showCoAuthors && (
                    <div className="px-4 pb-3 border-t border-gray-50">
                      {coAuthorsLoading ? (
                        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading co-authors...
                        </div>
                      ) : coAuthorsError ? (
                        <div className="py-3 text-sm" data-testid="coauthors-error">
                          <p className="text-red-500">Couldn't load co-authors.</p>
                          <button
                            onClick={loadCoAuthors}
                            className="mt-1 text-xs font-medium text-iflow-600 hover:text-iflow-700"
                          >
                            Retry
                          </button>
                        </div>
                      ) : coAuthors.length === 0 ? (
                        <p className="text-sm text-gray-500 italic py-3">No co-author data available.</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 pt-2">
                          {coAuthors.slice(0, 10).map((ca) => (
                            <div key={ca.entity_id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 text-left">
                              <div className="w-7 h-7 rounded-full bg-iflow-50 flex items-center justify-center shrink-0">
                                <span className="text-[9px] font-semibold text-iflow-600">
                                  {ca.name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium text-gray-800 truncate">{ca.name}</div>
                                {ca.affiliation && <div className="text-[10px] text-gray-500 truncate">{ca.affiliation}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer actions */}
          {entity && !loading && (
            <div className="flex items-center gap-2 p-4 border-t border-gray-100 shrink-0">
              <button
                onClick={() => setLinkModalOpen(true)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 transition-colors"
              >
                <Link2 className="w-4 h-4" />
                Link to Project
              </button>
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Project Selector Modal */}
      {entity && (
        <ProjectSelectorModal
          isOpen={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          entity={entity}
          onLinked={() => { setLinkModalOpen(false) }}
        />
      )}
    </>
  )
}
